import React, { useState, useEffect } from "react";
import { CalendarIcon, FileText, Download } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import MenuBar from "@/components/MenuBar";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { format } from "date-fns";
import { AgreementDownloadUrlResponse } from "@/types/employment-agreement";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { isNannyUser } from "@/types/user";

interface Agreement {
  id: number;
  employer_id: number;
  employee_id: number;
  pdf_path: string;
  agreement_data: any;
  created_at: string;
  status: string;
}

export default function NannyAgreementView() {
  const { toast } = useToast();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchAgreements();
  }, []);

  const fetchAgreements = async () => {
    try {
      setIsLoading(true);

      // Check if user is a nanny and has employerEmployeeId
      if (!user || !isNannyUser(user)) {
        throw new Error("Not authorized");
      }

      const response = await ApiService.request<{
        agreements: Agreement[];
        status: string;
      }>(EndpointKeys.LIST_EMPLOYEE_AGREEMENTS, {
        method: "GET",
        urlParams: { employee_id: user.employerEmployeeId },
      });

      if (response.status === "success" && Array.isArray(response.agreements)) {
        setAgreements(response.agreements);
      } else {
        throw new Error("Failed to fetch agreements");
      }
    } catch (error) {
      console.error("Error fetching agreements:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to load employment contracts",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadAgreement = async (agreementId: number) => {
    try {
      // Get the presigned URL for the agreement
      const response = await ApiService.request<AgreementDownloadUrlResponse>(
        EndpointKeys.GET_AGREEMENT_DOWNLOAD_URL,
        {
          method: "GET",
          queryParams: { agreement_id: agreementId },
        },
      );

      if (response.presignedUrl) {
        // Open the document in a new tab
        window.open(response.presignedUrl, "_blank");
      } else {
        throw new Error("Failed to get download URL for the agreement");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to download agreement",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy");
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <FileText className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>
      {/* Added margin-left to account for fixed sidebar */}
      <div className="flex-1 w-full md:w-auto">
        <div className="p-4 md:p-6 max-w-7xl space-y-6 mt-16 md:mt-0">
          <h2 className="text-2xl font-bold">Employment Contracts</h2>

          <Alert className="mb-4">
            <FileText className="h-4 w-4 text-primary" />
            <AlertTitle>Your Employment Contracts</AlertTitle>
            <AlertDescription>
              Here you can view and download your employment contracts. These
              documents outline the terms of your employment as agreed between
              you and your employer.
            </AlertDescription>
          </Alert>

          {isLoading ? (
            <Card>
              <CardHeader>
                <Skeleton className="h-8 w-1/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-10 w-1/4" />
              </CardContent>
            </Card>
          ) : agreements.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>No Contracts Available</CardTitle>
                <CardDescription>
                  You currently don't have any employment contracts. Please
                  contact your employer if you believe this is an error.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            agreements.map((agreement) => (
              <Card key={agreement.id} className="mb-4">
                <CardHeader>
                  <CardTitle>
                    Employment Contract - {formatDate(agreement.created_at)}
                  </CardTitle>
                  <CardDescription>
                    Status:{" "}
                    {agreement.status.charAt(0).toUpperCase() +
                      agreement.status.slice(1)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium">Employer</p>
                      <p className="text-sm text-muted-foreground">
                        {agreement.agreement_data.employerName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Start Date</p>
                      <p className="text-sm text-muted-foreground">
                        {agreement.agreement_data.startDate
                          ? formatDate(agreement.agreement_data.startDate)
                          : "Not specified"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Position</p>
                      <p className="text-sm text-muted-foreground">Nanny</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Salary</p>
                      <p className="text-sm text-muted-foreground">
                        £
                        {agreement.agreement_data.salaryAmount?.toLocaleString()}{" "}
                        per month
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => downloadAgreement(agreement.id)}
                    className="mt-4"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Contract
                  </Button>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
